/**
 * Auto-Gift Manager
 * Adds the free gift when the trigger product is in the cart and removes it when
 * the trigger is removed (inline with cart line updates + reconcile on load/refresh).
 */

const AUTO_GIFT_SOURCE = 'auto-gift-manager';

class AutoGiftManager {
  #processing = false;
  #pendingReconcile = false;
  #debounceTimer = null;
  #userRemovedGift = false;
  #lastCartSnapshot = null;

  constructor(config) {
    this.enabled = config.enabled;
    this.triggerProductId = String(config.triggerProductId || '');
    this.giftProductId = String(config.giftProductId || '');
    this.giftVariantId = config.giftVariantId;
    this.quantityMode = config.quantityMode || 'fixed';

    if (!this.enabled || !this.triggerProductId || !this.giftProductId || !this.giftVariantId) {
      return;
    }

    document.addEventListener('cart:update', (event) => {
      if (event.detail?.data?.source === AUTO_GIFT_SOURCE) return;
      const delay =
        event.detail?.data?.source === 'cart-items-component' ||
        event.detail?.data?.source === 'product-form-component' ||
        event.detail?.data?.source === 'product-main-bundle'
          ? 300
          : 120;
      this.#scheduleReconcile(delay);
    });

    document.addEventListener('cart:updated', () => this.#scheduleReconcile(120));

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#scheduleReconcile(0));
    } else {
      this.#scheduleReconcile(0);
    }
  }

  get isBusy() {
    return this.#processing;
  }

  willSyncTriggerLine() {
    return true;
  }

  #scheduleReconcile(delay = 120) {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => this.#reconcile(), delay);
  }

  #cartRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  #getSectionIds(sectionIds) {
    if (sectionIds?.length) return sectionIds;

    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    if (ids.size === 0) ids.add('cart-drawer-standalone');
    return Array.from(ids);
  }

  /** @param {object} item */
  #isGiftItem(item) {
    const props = item.properties || {};
    if (props._auto_gift === 'true') return true;
    return String(item.product_id) === this.giftProductId;
  }

  /** @param {object[]} items */
  #findGiftItems(items) {
    return (items || []).filter((item) => this.#isGiftItem(item));
  }

  /** @param {object[]} items */
  #getTriggerQuantity(items, overrides = {}) {
    return (items || []).reduce((sum, item) => {
      if (this.#isGiftItem(item)) return sum;
      if (String(item.product_id) !== this.triggerProductId) return sum;

      const qty =
        overrides[item.key] !== undefined
          ? Math.max(parseInt(String(overrides[item.key]), 10) || 0, 0)
          : parseInt(String(item.quantity), 10) || 0;

      return sum + qty;
    }, 0);
  }

  /** @param {object[]} items */
  #detectManualGiftRemoval(items, triggerQty, giftItems) {
    if (!this.#lastCartSnapshot) return;

    const hadTrigger = this.#getTriggerQuantity(this.#lastCartSnapshot.items) > 0;
    const hadGift = this.#findGiftItems(this.#lastCartSnapshot.items).length > 0;

    if (hadTrigger && hadGift && triggerQty > 0 && giftItems.length === 0) {
      this.#userRemovedGift = true;
    }

    if (triggerQty <= 0) {
      this.#userRemovedGift = false;
    }
  }

  /** @param {Record<string, number>} updates @param {string[]} sectionIds */
  async #postCartUpdate(updates, sectionIds) {
    const ids = this.#getSectionIds(sectionIds);
    const response = await fetch(`${this.#cartRoot()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates,
        sections: ids.join(','),
        sections_url: window.location.pathname,
      }),
    });

    const responseData = await response.json();
    if (!response.ok) {
      console.error('[AutoGift] Update failed:', responseData?.description || responseData);
      return null;
    }

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: responseData,
          sourceId: AUTO_GIFT_SOURCE,
          data: {
            source: AUTO_GIFT_SOURCE,
            itemCount: responseData.item_count,
            sections: responseData.sections || {},
          },
        },
      })
    );

    return responseData;
  }

  /**
   * Remove or sync gift lines when a trigger product line changes quantity.
   * @param {number} lineIndex
   * @param {number} newQty
   * @param {string[]} sectionIds
   * @param {string} [lineKey]
   */
  async syncTriggerLineQuantity(lineIndex, newQty, sectionIds, lineKey) {
    this.#processing = true;

    try {
      const response = await fetch(`${this.#cartRoot()}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;

      const cart = await response.json();
      const items = cart.items || [];
      const mainItem = lineKey ? items.find((item) => item.key === lineKey) : items[lineIndex - 1];

      if (!mainItem || String(mainItem.product_id) !== this.triggerProductId || this.#isGiftItem(mainItem)) {
        return null;
      }

      const nextMainQty = Math.max(parseInt(String(newQty), 10) || 0, 0);
      /** @type {Record<string, number>} */
      const updates = { [mainItem.key]: nextMainQty };

      const triggerQtyAfter = this.#getTriggerQuantity(items, { [mainItem.key]: nextMainQty });
      const giftItems = this.#findGiftItems(items);
      const requiredGiftQty =
        this.quantityMode === 'match' ? Math.max(triggerQtyAfter, 0) : triggerQtyAfter > 0 ? 1 : 0;

      giftItems.forEach((gift) => {
        if (triggerQtyAfter <= 0) {
          updates[gift.key] = 0;
        } else if (!this.#userRemovedGift && gift.quantity !== requiredGiftQty) {
          updates[gift.key] = requiredGiftQty;
        }
      });

      const onlyMainUpdate =
        Object.keys(updates).length === 1 && updates[mainItem.key] === nextMainQty;

      if (onlyMainUpdate) {
        return null;
      }

      const cartData = await this.#postCartUpdate(updates, sectionIds);
      if (cartData && triggerQtyAfter <= 0) {
        this.#userRemovedGift = false;
      }

      this.#lastCartSnapshot = cartData ? { items: cartData.items || [] } : null;
      return cartData;
    } catch (error) {
      console.error('[AutoGift] Trigger line sync failed:', error);
      return null;
    } finally {
      this.#processing = false;
    }
  }

  async #reconcile() {
    if (this.#processing) {
      this.#pendingReconcile = true;
      return;
    }

    this.#processing = true;

    try {
      const response = await fetch(`${this.#cartRoot()}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const cart = await response.json();
      const items = cart.items || [];
      const triggerQty = this.#getTriggerQuantity(items);
      const giftItems = this.#findGiftItems(items);

      this.#detectManualGiftRemoval(items, triggerQty, giftItems);

      /** @type {Record<string, number>} */
      const updates = {};

      if (triggerQty <= 0) {
        giftItems.forEach((gift) => {
          updates[gift.key] = 0;
        });
        this.#userRemovedGift = false;
      } else if (!this.#userRemovedGift) {
        const requiredGiftQty = this.quantityMode === 'match' ? triggerQty : 1;

        if (giftItems.length === 0) {
          const sectionIds = this.#getSectionIds();
          const addResponse = await fetch(`${this.#cartRoot()}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [
                {
                  id: this.giftVariantId,
                  quantity: requiredGiftQty,
                  properties: {
                    _auto_gift: 'true',
                    _auto_gift_trigger: this.triggerProductId,
                  },
                },
              ],
              sections: sectionIds.join(','),
              sections_url: window.location.pathname,
            }),
          });

          const addData = await addResponse.json();
          if (!addResponse.ok) {
            console.error('[AutoGift] Add failed:', addData?.description || addData);
            return;
          }

          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: addData,
                sourceId: AUTO_GIFT_SOURCE,
                data: {
                  source: AUTO_GIFT_SOURCE,
                  itemCount: addData.item_count,
                  sections: addData.sections || {},
                },
              },
            })
          );

          this.#lastCartSnapshot = { items: addData.items || [] };
          return;
        }

        giftItems.forEach((gift) => {
          if (gift.quantity !== requiredGiftQty) {
            updates[gift.key] = requiredGiftQty;
          }
        });
      }

      if (Object.keys(updates).length > 0) {
        const cartData = await this.#postCartUpdate(updates, []);
        this.#lastCartSnapshot = cartData ? { items: cartData.items || [] } : { items };
        return;
      }

      this.#lastCartSnapshot = { items };
    } catch (error) {
      console.error('[AutoGift] Reconcile failed:', error);
    } finally {
      this.#processing = false;
      if (this.#pendingReconcile) {
        this.#pendingReconcile = false;
        this.#scheduleReconcile(0);
      }
    }
  }
}

function initAutoGift() {
  if (window.autoGiftConfig) {
    window.autoGiftManager = new AutoGiftManager(window.autoGiftConfig);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAutoGift);
} else {
  initAutoGift();
}
